import React, { useState, useContext, useMemo, useCallback, useRef } from "react";
import { Drawer, Modal, Tooltip } from "antd";
import EndChatWithAiSessionModal from "../EndChatWithAiSessionModal";
import { isCurrentAgentServingVisitor, shouldConfirmEndEntireSession } from "../../helpers/agentSessionEnd";
import { CloseOutlined, MinusOutlined, UserOutlined } from "@ant-design/icons";
import ReactCountryFlag from "react-country-flag";
import { FaChrome, FaWindows, FaApple, FaLinux } from "react-icons/fa";

import DrageableBody from "../drageableBody";
import { useDispatch, useSelector } from "react-redux";
import { SocketContext } from "../../context/socketContext";
import { onLeaveServed } from "../../redux/VisitorsJoined/VisitorsJoinedActions";
import {
  dedupeActiveVisitors,
  normalizeVisitorUniqueId,
  visitorsUniqueKeyEqual,
} from "../../helpers/visitorId";

/** Panel width; cap on small viewports — keep in sync with bottom tab bar inset */
export const VISITOR_CHAT_DRAWER_WIDTH = "min(680px, 92vw)";
/** Narrow mode — more room for the main dashboard */
export const VISITOR_CHAT_DRAWER_WIDTH_COMPACT = "min(420px, 88vw)";

/**
 * Right-side chat drawer — visitor chat + profile (Zendesk-style chrome).
 * No backdrop mask so agents can see and interact with the rest of the dashboard while it is open.
 */
export default function DragableModal({
  AllActiveVisitors,
  setAllActiveVisitors,
  visitorDrawerCompact = true,
  onVisitorDrawerCompactChange,
}) {
  const dispatch = useDispatch();
  const servedData = useSelector((state) => state.VisitorReducer.data);
  const { Socket } = useContext(SocketContext);
  const userData = useSelector((state) => state.Auth.userData);
  const [endSessionConfirmVisitor, setEndSessionConfirmVisitor] = useState(null);
  const [drawerHeaderActions, setDrawerHeaderActions] = useState(null);
  const drawerHeaderActionsOwnerRef = useRef(null);
  const uniqueActiveVisitors = useMemo(
    () => dedupeActiveVisitors(AllActiveVisitors),
    [AllActiveVisitors]
  );

  const focusedVisitor = useMemo(() => {
    if (!uniqueActiveVisitors?.length) return null;
    const byZ = uniqueActiveVisitors.find((v) => v.zIndex === 1);
    if (byZ && !byZ.minimized) return byZ;
    const firstOpen = uniqueActiveVisitors.find((v) => !v.minimized);
    if (firstOpen) return firstOpen;
    return uniqueActiveVisitors[0];
  }, [uniqueActiveVisitors]);

  const registerDrawerHeaderActions = useCallback(
    (visitorId, node) => {
      if (visitorId && visitorsUniqueKeyEqual(focusedVisitor?.unique_id, visitorId)) {
        drawerHeaderActionsOwnerRef.current = visitorId;
        setDrawerHeaderActions(node);
      }
    },
    [focusedVisitor?.unique_id]
  );

  const clearDrawerHeaderActions = useCallback((visitorId) => {
    if (drawerHeaderActionsOwnerRef.current === visitorId) {
      drawerHeaderActionsOwnerRef.current = null;
      setDrawerHeaderActions(null);
    }
  }, []);

  const hasVisitors = AllActiveVisitors.length > 0;
  const anyNotMinimized = AllActiveVisitors.some((v) => !v.minimized);
  const drawerOpen = hasVisitors && anyNotMinimized;

  const drawerWidth = visitorDrawerCompact ? VISITOR_CHAT_DRAWER_WIDTH_COMPACT : VISITOR_CHAT_DRAWER_WIDTH;

  const toggleDrawerWidthMode = useCallback(() => {
    if (typeof onVisitorDrawerCompactChange !== "function") return;
    onVisitorDrawerCompactChange((prev) => !prev);
  }, [onVisitorDrawerCompactChange]);

  const leaveRoom = useCallback(
    (visitor, { endEntireChat = false } = {}) => {
      if (!Socket) return;
      Socket.emit("agentLeaveRoom", {
        agentId: userData?._id,
        unique_id: visitor?.unique_id,
        endEntireChat: Boolean(endEntireChat),
      });
    },
    [Socket, userData?._id]
  );

  const completeVisitorPanelClose = useCallback(
    (currentVisitor, { endEntireChat = false } = {}) => {
      if (
        currentVisitor?.current_conversation?.agentParticipants?.some(
          (x) => String(x?._id) === String(userData?._id)
        )
      ) {
        leaveRoom(currentVisitor, { endEntireChat });
      }
      dispatch(onLeaveServed(currentVisitor, servedData));
      setAllActiveVisitors((visitors) => {
        const toggled = visitors?.map((visitor) =>
          visitorsUniqueKeyEqual(visitor.unique_id, currentVisitor?.unique_id)
            ? { ...visitor, isOpen: !visitor.isOpen }
            : visitor
        );
        const wasInList = visitors?.some((v) =>
          visitorsUniqueKeyEqual(v?.unique_id, currentVisitor?.unique_id)
        );
        if (wasInList) {
          return toggled?.filter(
            (visitor) => !visitorsUniqueKeyEqual(visitor.unique_id, currentVisitor?.unique_id)
          );
        }
        return toggled;
      });
    },
    [dispatch, leaveRoom, servedData, userData?._id]
  );

  const toggleModal = (currentVisitor) => {
    if (shouldConfirmEndEntireSession(currentVisitor, userData?._id)) {
      setEndSessionConfirmVisitor(currentVisitor);
      return;
    }
    if (isCurrentAgentServingVisitor(currentVisitor, userData?._id)) {
      Modal.confirm({
        title: "End this chat?",
        content:
          "You are serving this visitor. Close the drawer to end the chat and stop serving this conversation?",
        okText: "End chat",
        cancelText: "Stay",
        zIndex: 1300,
        centered: true,
        onOk: () => completeVisitorPanelClose(currentVisitor, { endEntireChat: false }),
      });
      return;
    }
    completeVisitorPanelClose(currentVisitor, { endEntireChat: false });
  };

  const toggleMinimized = (id) => {
    setAllActiveVisitors((visitors) =>
      visitors.map((visitor) =>
        visitorsUniqueKeyEqual(visitor.unique_id, id) ? { ...visitor, minimized: true, active: false } : visitor
      )
    );
  };

  const handleDrawerClose = () => {
    if (focusedVisitor) toggleModal(focusedVisitor);
  };

  const visitorLabel = focusedVisitor
    ? focusedVisitor?.name
      ? `Visitor ${focusedVisitor.name}`
      : `Visitor ${normalizeVisitorUniqueId(focusedVisitor?.unique_id) || String(focusedVisitor?.unique_id ?? "")}`
    : "";

  const countryCode =
    focusedVisitor?.location?.country && String(focusedVisitor.location.country).length === 2
      ? String(focusedVisitor.location.country).toUpperCase()
      : null;

  const platformStr = (focusedVisitor?.platform || "").toLowerCase();
  const PlatformIcon =
    platformStr.includes("mac") || platformStr.includes("ios")
      ? FaApple
      : platformStr.includes("linux")
        ? FaLinux
        : FaWindows;

  const drawerTitle = focusedVisitor && (
    <div className="chatVisitorDrawer__titleBar">
      <div className="chatVisitorDrawer__titleRow">
        <div className="chatVisitorDrawer__avatar" aria-hidden>
          <UserOutlined />
        </div>
        <div className="chatVisitorDrawer__identity">
          <span className="chatVisitorDrawer__visitorName">{visitorLabel}</span>
          <div className="chatVisitorDrawer__metaIcons">
            {countryCode ? (
              <span className="chatVisitorDrawer__flag" title={focusedVisitor?.location?.country}>
                <ReactCountryFlag countryCode={countryCode} svg style={{ width: "1.1em", height: "1.1em" }} />
              </span>
            ) : null}
            <span className="chatVisitorDrawer__metaIcon" title={focusedVisitor?.platform || "Platform"}>
              <PlatformIcon />
            </span>
            <span className="chatVisitorDrawer__metaIcon" title={focusedVisitor?.browser?.name || "Browser"}>
              <FaChrome />
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const endChatVisitorName =
    endSessionConfirmVisitor?.name ||
    normalizeVisitorUniqueId(endSessionConfirmVisitor?.unique_id) ||
    null;

  return (
    <>
      <EndChatWithAiSessionModal
        open={Boolean(endSessionConfirmVisitor)}
        visitorName={endChatVisitorName}
        onCancel={() => setEndSessionConfirmVisitor(null)}
        onConfirm={() => {
          const ref = endSessionConfirmVisitor;
          setEndSessionConfirmVisitor(null);
          if (!ref) return;
          const current =
            AllActiveVisitors?.find((x) => visitorsUniqueKeyEqual(x?.unique_id, ref.unique_id)) ?? ref;
          const endEntire = shouldConfirmEndEntireSession(current, userData?._id);
          completeVisitorPanelClose(current, { endEntireChat: endEntire });
        }}
      />
      <Drawer
        rootClassName="chatVisitorDrawer"
        title={drawerTitle}
        extra={
          focusedVisitor ? (
            <div className="chatVisitorDrawer__headerActions">
              {drawerHeaderActions}
              <Tooltip placement="bottom" title="Minimize">
                <button
                  type="button"
                  className="chatVisitorDrawer__winBtn"
                  aria-label="Minimize"
                  onClick={() => toggleMinimized(focusedVisitor.unique_id)}
                >
                  <MinusOutlined />
                </button>
              </Tooltip>
              <Tooltip placement="bottom" title="Close">
                <button
                  type="button"
                  className="chatVisitorDrawer__winBtn"
                  aria-label="Close"
                  onClick={() => toggleModal(focusedVisitor)}
                >
                  <CloseOutlined />
                </button>
              </Tooltip>
            </div>
          ) : null
        }
        placement="right"
        width={drawerWidth}
        onClose={handleDrawerClose}
        open={drawerOpen}
        closable={false}
        mask={false}
        maskClosable={false}
        destroyOnClose={false}
        zIndex={1200}
        push={false}
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          minWidth: 0,
          maxWidth: "100%",
          overflow: "hidden",
        }}
        contentWrapperStyle={{ overflowX: "hidden", maxWidth: "100%" }}
        headerStyle={{
          flexGrow: 0,
          flexShrink: 0,
          flexBasis: "auto",
          overflow: "visible",
          alignItems: "center",
        }}
        drawerStyle={{
          minWidth: 0,
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
        bodyStyle={{
          padding: 0,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflow: "hidden",
          overflowX: "hidden",
        }}
      >
        {uniqueActiveVisitors.map((visitor) => {
          const isFocused = visitorsUniqueKeyEqual(focusedVisitor?.unique_id, visitor?.unique_id);
          const rowKey = normalizeVisitorUniqueId(visitor?.unique_id) || visitor?.unique_id;
          return (
            <div
              key={rowKey}
              className={`${visitor?.zIndex === 1 ? "multiModalFocus" : "multiModalAway"} ${rowKey} increaseSize`}
              style={{
                display: isFocused ? "flex" : "none",
                flex: 1,
                flexDirection: "column",
                minHeight: 0,
                minWidth: 0,
                height: "100%",
                boxSizing: "border-box",
              }}
            >
              <div
                className="body"
                style={{
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  overflowX: "hidden",
                  overflowY: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  boxSizing: "border-box",
                }}
              >
                <DrageableBody
                  currentVisitor={visitor}
                  isDrawerTarget={isFocused}
                  registerDrawerHeaderActions={registerDrawerHeaderActions}
                  clearDrawerHeaderActions={clearDrawerHeaderActions}
                  visitorDrawerCompact={visitorDrawerCompact}
                  onToggleDrawerWidth={toggleDrawerWidthMode}
                />
              </div>
            </div>
          );
        })}
      </Drawer>
    </>
  );
}
